/**
 * VeneerForm component - handles veneer product CRUD operations
 * Supports both single veneer add (for natural veneers) and collection add (for hybrid with many codes)
 */
import React, { useState, useEffect } from 'react';
import { api } from '../../lib/store';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { Plus, X, Package } from 'lucide-react';
import { formatSizeInput } from '../../lib/utils';
import { logger } from '../../lib/logger';

const VENEER_SPECIES = ['Walnut', 'Teak', 'Oak', 'Ash', 'Cherry', 'Maple', 'Mahogany', 'Ebony', 'Rosewood', 'Wenge', 'Zebrano', 'Pine', 'Beech', 'Birch', 'Cedar', 'Elm', 'Mango', 'Sapele', 'Sycamore', 'Padauk', 'Bubinga'];
const VENEER_SIZES = [
  { id: '8x4', label: '8x4 ft', sqft: 32 },
  { id: '10x4', label: '10x4 ft', sqft: 40 },
];

// Empty veneer for form reset
const emptyVeneer = {
  brand_name: '',
  subcategory: '',
  code_prefix: '',  // Product-level prefix like "LH" for Legend Hybrid
  product_code: '',
  veneer_type: '',  // Now used for species like Walnut, Teak, Oak
  rate_8x4: '',
  rate_10x4: '',
  rate_custom: '',
  custom_size: '',
  stock_sheets: '',
  show_brand_in_pdf: true,
};

export default function VeneerForm({ open, onOpenChange, editProduct, onSuccess }) {
  const [form, setForm] = useState({ ...emptyVeneer });
  const [saving, setSaving] = useState(false);
  const [allBrands, setAllBrands] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [subcatSuggestions, setSubcatSuggestions] = useState([]);
  const [veneerTypeSuggestions, setVeneerTypeSuggestions] = useState([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showSubcatDropdown, setShowSubcatDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  
  // Collection mode for adding multiple codes at once
  const [collectionMode, setCollectionMode] = useState(false);
  const [collectionCodes, setCollectionCodes] = useState([{ code: '', rate_8x4: '', rate_10x4: '', custom_rates: {}, stock: '' }]);
  
  // Available sizes selection (for collection mode) - supports MULTIPLE custom sizes
  const [selectedSizes, setSelectedSizes] = useState(['8x4', '10x4']); // Default to both standard sizes
  const [customCollectionSizes, setCustomCollectionSizes] = useState([]); // Array of custom sizes like ['9.5x4', '12x4']
  const [newCollectionCustomSize, setNewCollectionCustomSize] = useState(''); // Input for new custom size
  const [showCustomSizeInput, setShowCustomSizeInput] = useState(false); // Show custom size input
  
  // Custom sizes for single veneer mode (can have multiple)
  const [customSizes, setCustomSizes] = useState([]); // Array of {size: '9.5x4', rate: ''}
  const [newCustomSize, setNewCustomSize] = useState('');

  // Load brands and subcategories for Veneers
  useEffect(() => {
    api.getBrands().then(res => setAllBrands(res.data || [])).catch((err) => logger.warn('Failed to load brands:', err));
    api.getSubcategories('Veneers').then(res => setAllSubcategories(res.data || [])).catch((err) => logger.warn('Failed to load subcategories:', err));
  }, []);

  // Load edit product data
  useEffect(() => {
    if (editProduct) {
      setForm({
        brand_name: editProduct.brand_name || '',
        subcategory: editProduct.subcategory || '',
        code_prefix: editProduct.code_prefix || '',
        product_code: editProduct.product_code || '',
        veneer_type: editProduct.veneer_type || '',  // Species is optional
        rate_8x4: editProduct.rate_8x4 || '',
        rate_10x4: editProduct.rate_10x4 || '',
        rate_custom: editProduct.rate_custom || '',
        custom_size: editProduct.custom_size || '',
        stock_sheets: editProduct.stock_sheets || '',
        show_brand_in_pdf: editProduct.show_brand_in_pdf !== false,
      });
      
      // Set custom size flag
      // (legacy: `useCustomSize` flag was removed when collection mode took
      // over — keeping the field on `editProduct` round-trips fine without it.)
      
      // Check if this is a collection (has codes array or multiple variants)
      if (editProduct.isVeneerCollection && editProduct.codes?.length > 0) {
        // Edit mode for collection - load all codes
        setCollectionMode(true);
        setCollectionCodes(editProduct.codes.map(c => ({
          code: c.code || '',
          rate_8x4: c.rate_8x4 || '',
          rate_10x4: c.rate_10x4 || '',
          rate_custom: c.rate_custom || '',
          stock: c.stock_sheets || '',
          id: c.id, // Keep ID for updates
        })));
        // Set available sizes from first code
        const firstCode = editProduct.codes[0];
        const sizes = [];
        if (firstCode.rate_8x4) sizes.push('8x4');
        if (firstCode.rate_10x4) sizes.push('10x4');
        if (firstCode.custom_size) {
          sizes.push(firstCode.custom_size);
          setCustomCollectionSizes([firstCode.custom_size]);
        }
        setSelectedSizes(sizes.length > 0 ? sizes : ['8x4', '10x4']);
      } else if (editProduct.variants?.length > 1) {
        // Old format - multiple variants
        setCollectionMode(true);
        setCollectionCodes(editProduct.variants.map(v => ({
          code: v.product_code || '',
          rate_8x4: v.rate_8x4 || '',
          rate_10x4: v.rate_10x4 || '',
          rate_custom: v.rate_custom || '',
          stock: v.stock_sheets || '',
          id: v.id,
        })));
        setSelectedSizes(['8x4', '10x4']);
      } else {
        // Single veneer edit
        setCollectionMode(false);
        setCollectionCodes([{ code: '', rate_8x4: '', rate_10x4: '', rate_custom: '', stock: '' }]);
        setSelectedSizes(['8x4', '10x4']);
        // Load custom sizes if present
        if (editProduct.custom_sizes && editProduct.custom_sizes.length > 0) {
          setCustomSizes(editProduct.custom_sizes);
        } else {
          setCustomSizes([]);
        }
      }
    } else {
      setForm({ ...emptyVeneer });
      setCollectionMode(false);
      setCollectionCodes([{ code: '', rate_8x4: '', rate_10x4: '', custom_rates: {}, stock: '' }]);
      setSelectedSizes(['8x4', '10x4']);
      setCustomCollectionSizes([]);
      setNewCollectionCustomSize('');
      setShowCustomSizeInput(false);
      setCustomSizes([]);
      setNewCustomSize('');
    }
  }, [editProduct, open]);

  const handleBrandInput = (value) => {
    setForm(f => ({ ...f, brand_name: value }));
    if (value.length > 0) {
      const filtered = allBrands.filter(b => b.toLowerCase().includes(value.toLowerCase())).slice(0, 6);
      setBrandSuggestions(filtered);
      setShowBrandDropdown(filtered.length > 0);
    } else {
      setShowBrandDropdown(false);
    }
  };

  const handleSubcatInput = (value) => {
    setForm(f => ({ ...f, subcategory: value }));
    if (value.length > 0) {
      const filtered = allSubcategories.filter(s => s.toLowerCase().includes(value.toLowerCase())).slice(0, 6);
      setSubcatSuggestions(filtered);
      setShowSubcatDropdown(filtered.length > 0);
    } else {
      setShowSubcatDropdown(false);
    }
  };

  const handleVeneerTypeInput = (value) => {
    setForm(f => ({ ...f, veneer_type: value }));
    if (value.length > 0) {
      const filtered = VENEER_SPECIES.filter(t => t.toLowerCase().includes(value.toLowerCase()));
      setVeneerTypeSuggestions(filtered);
      setShowTypeDropdown(filtered.length > 0);
    } else {
      // Show all suggestions when empty
      setVeneerTypeSuggestions(VENEER_SPECIES);
      setShowTypeDropdown(true);
    }
  };

  const addCollectionCode = () => {
    setCollectionCodes([...collectionCodes, { code: '', rate_8x4: '', rate_10x4: '', custom_rates: {}, stock: '' }]);
  };

  const removeCollectionCode = (index) => {
    if (collectionCodes.length > 1) {
      setCollectionCodes(collectionCodes.filter((_, i) => i !== index));
    }
  };

  const updateCollectionCode = (index, field, value) => {
    const updated = [...collectionCodes];
    updated[index][field] = value;
    setCollectionCodes(updated);
  };
  
  // Update custom rate for a specific size in collection mode
  const updateCollectionCodeCustomRate = (index, size, value) => {
    const updated = [...collectionCodes];
    if (!updated[index].custom_rates) {
      updated[index].custom_rates = {};
    }
    updated[index].custom_rates[size] = value;
    setCollectionCodes(updated);
  };
  
  // Add a new custom size for collection mode
  const addCustomCollectionSize = () => {
    const formattedSize = formatSizeInput(newCollectionCustomSize);
    if (formattedSize && !customCollectionSizes.includes(formattedSize) && !['8x4', '10x4'].includes(formattedSize)) {
      setCustomCollectionSizes([...customCollectionSizes, formattedSize]);
      setSelectedSizes([...selectedSizes, formattedSize]);
      setNewCollectionCustomSize('');
    }
  };
  
  // Remove a custom size from collection mode
  const removeCustomCollectionSize = (size) => {
    setCustomCollectionSizes(customCollectionSizes.filter(s => s !== size));
    setSelectedSizes(selectedSizes.filter(s => s !== size));
  };

  const handleSave = async () => {
    if (!form.brand_name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    if (!form.subcategory.trim()) {
      toast.error('Subcategory/Collection name is required');
      return;
    }
    // Note: code_prefix and veneer_type (species) are optional

    setSaving(true);
    try {
      if (collectionMode) {
        // Collection mode - add/update multiple veneers at once
        const validCodes = collectionCodes.filter(c => c.code.trim() && (c.rate_8x4 || c.rate_10x4));
        if (validCodes.length === 0) {
          toast.error('Please add at least one code with a rate');
          setSaving(false);
          return;
        }

        // If editing a collection, update existing and create new
        if (editProduct?.isVeneerCollection || editProduct?.variants?.length > 1) {
          let updated = 0;
          let created = 0;
          
          for (const codeItem of validCodes) {
            const productData = {
              brand_name: form.brand_name.trim(),
              main_category: 'Veneers',
              subcategory: form.subcategory.trim(),
              product_name: form.subcategory.trim(),
              product_code: codeItem.code.trim(),
              veneer_type: form.veneer_type || '',
              rate_type: 'per sq ft',
              rate_8x4: parseFloat(codeItem.rate_8x4) || 0,
              rate_10x4: parseFloat(codeItem.rate_10x4) || 0,
              base_rate: parseFloat(codeItem.rate_8x4) || parseFloat(codeItem.rate_10x4) || 0,
              stock_sheets: parseInt(codeItem.stock) || 0,
              gst_included: true,
              status: 'active',
              show_brand_in_pdf: form.show_brand_in_pdf !== false,
            };
            
            if (codeItem.id) {
              // Update existing code
              await api.updateProduct(codeItem.id, productData);
              updated++;
            } else {
              // Create new code
              await api.createProduct(productData);
              created++;
            }
          }
          
          // Delete removed codes
          const originalIds = (editProduct.codes || editProduct.variants || []).map(c => c.id);
          const currentIds = validCodes.filter(c => c.id).map(c => c.id);
          const deletedIds = originalIds.filter(id => !currentIds.includes(id));
          
          for (const id of deletedIds) {
            try {
              await api.deleteProduct(id);
            } catch (e) {
              logger.error('Failed to delete removed code:', e);
            }
          }
          
          const messages = [];
          if (updated > 0) messages.push(`${updated} updated`);
          if (created > 0) messages.push(`${created} added`);
          if (deletedIds.length > 0) messages.push(`${deletedIds.length} removed`);
          toast.success(`Collection "${form.subcategory}": ${messages.join(', ')}`);
        } else {
          // Creating new collection
          let created = 0;
          for (const codeItem of validCodes) {
            await api.createProduct({
              brand_name: form.brand_name.trim(),
              main_category: 'Veneers',
              subcategory: form.subcategory.trim(),
              product_name: form.subcategory.trim(),
              product_code: codeItem.code.trim(),
              veneer_type: form.veneer_type || '',
              rate_type: 'per sq ft',
              rate_8x4: parseFloat(codeItem.rate_8x4) || 0,
              rate_10x4: parseFloat(codeItem.rate_10x4) || 0,
              base_rate: parseFloat(codeItem.rate_8x4) || parseFloat(codeItem.rate_10x4) || 0,
              stock_sheets: parseInt(codeItem.stock) || 0,
              gst_included: true,
              status: 'active',
              show_brand_in_pdf: form.show_brand_in_pdf !== false,
            });
            created++;
          }
          toast.success(`Created ${created} veneers in ${form.subcategory}`);
        }
      } else {
        // Single veneer mode
        if (!form.product_code.trim()) {
          toast.error('Veneer code is required');
          setSaving(false);
          return;
        }
        if (!form.rate_8x4 && !form.rate_10x4) {
          toast.error('Please enter at least one rate (8x4 or 10x4)');
          setSaving(false);
          return;
        }

        const productData = {
          brand_name: form.brand_name.trim(),
          main_category: 'Veneers',
          subcategory: form.subcategory.trim(),
          product_name: form.subcategory.trim(),
          product_code: form.product_code.trim(),
          veneer_type: form.veneer_type || '',
          rate_type: 'per sq ft',
          rate_8x4: parseFloat(form.rate_8x4) || 0,
          rate_10x4: parseFloat(form.rate_10x4) || 0,
          base_rate: parseFloat(form.rate_8x4) || parseFloat(form.rate_10x4) || 0,
          stock_sheets: parseInt(form.stock_sheets) || 0,
          gst_included: true,
          status: 'active',
          show_brand_in_pdf: form.show_brand_in_pdf !== false,
        };

        if (editProduct?.id) {
          await api.updateProduct(editProduct.id, productData);
          toast.success('Veneer updated successfully');
        } else {
          await api.createProduct(productData);
          toast.success('Veneer added successfully');
        }
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save veneer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#1A1C16]">
            {editProduct?.isVeneerCollection || editProduct?.variants?.length > 1 
              ? `Edit Collection: ${editProduct.subcategory}` 
              : editProduct 
                ? 'Edit Veneer' 
                : collectionMode 
                  ? 'Add Veneer Collection' 
                  : 'Add Veneer'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode Toggle - Only show when adding new (not editing) */}
          {!editProduct && (
            <div className="flex gap-2 p-2 bg-[#F2F4EF] rounded-lg">
              <Button
                type="button"
                variant={!collectionMode ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCollectionMode(false)}
                className={!collectionMode ? 'bg-[#556B2F] text-white' : ''}
              >
                <Plus className="w-4 h-4 mr-1" /> Single Veneer
              </Button>
              <Button
                type="button"
                variant={collectionMode ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCollectionMode(true)}
                className={collectionMode ? 'bg-[#556B2F] text-white' : ''}
              >
                <Package className="w-4 h-4 mr-1" /> Collection (Multiple Codes)
              </Button>
            </div>
          )}

          {/* Brand Name */}
          <div className="relative">
            <Label className="text-xs font-semibold">Brand Name *</Label>
            <Input
              value={form.brand_name}
              onChange={e => handleBrandInput(e.target.value)}
              onFocus={() => {
                if (allBrands.length > 0 && !form.brand_name) {
                  setBrandSuggestions(allBrands.slice(0, 8));
                  setShowBrandDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowBrandDropdown(false), 200)}
              placeholder="e.g., ENGINEER PLY, TIMEX, FURROW"
              className="mt-1"
            />
            {showBrandDropdown && brandSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-[#E2E6D9] rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                {brandSuggestions.map(b => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, brand_name: b })); setShowBrandDropdown(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F2F4EF] text-sm"
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Subcategory/Collection */}
          <div className="relative">
            <Label className="text-xs font-semibold">Subcategory / Collection *</Label>
            <Input
              value={form.subcategory}
              onChange={e => handleSubcatInput(e.target.value)}
              onFocus={() => {
                if (allSubcategories.length > 0 && !form.subcategory) {
                  setSubcatSuggestions(allSubcategories.slice(0, 8));
                  setShowSubcatDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowSubcatDropdown(false), 200)}
              placeholder="e.g., Furo Reganto, LIGNUM VENEER"
              className="mt-1"
            />
            {showSubcatDropdown && subcatSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-[#E2E6D9] rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                {subcatSuggestions.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, subcategory: s })); setShowSubcatDropdown(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F2F4EF] text-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Veneer Species - Input with suggestions (OPTIONAL) */}
          <div className="relative">
            <Label className="text-xs font-semibold">Veneer Species <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              value={form.veneer_type}
              onChange={e => handleVeneerTypeInput(e.target.value)}
              onFocus={() => {
                if (!form.veneer_type) {
                  setVeneerTypeSuggestions(VENEER_SPECIES);
                  setShowTypeDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowTypeDropdown(false), 200)}
              placeholder="e.g., Walnut, Teak, Oak, Ash..."
              className="mt-1"
            />
            {showTypeDropdown && veneerTypeSuggestions.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-[#E2E6D9] rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
                {veneerTypeSuggestions.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setForm(f => ({ ...f, veneer_type: t })); setShowTypeDropdown(false); }}
                    className="w-full text-left px-3 py-2 hover:bg-[#F2F4EF] text-sm"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            <p className="text-[10px] text-[#8F9485] mt-1">
              Select from suggestions or type a custom species
            </p>
          </div>

          <Separator />

          {/* Single Veneer Mode */}
          {!collectionMode && (
            <>
              {/* Veneer Code */}
              <div>
                <Label className="text-xs font-semibold">Veneer Code *</Label>
                <Input
                  value={form.product_code}
                  onChange={e => setForm(f => ({ ...f, product_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., LH-EP101, HB 4001, R1001"
                  className="mt-1 font-mono"
                />
                <p className="text-[10px] text-[#8F9485] mt-1">
                  Full code that will appear in quotations
                </p>
              </div>

              {/* Rates */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold">Rate for 8x4 (₹/sq ft)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={form.rate_8x4}
                      onChange={e => setForm(f => ({ ...f, rate_8x4: e.target.value.replace(/[^0-9.]/g, '') }))}
                      placeholder="e.g., 165"
                      className="mt-1 font-mono-data"
                    />
                    <p className="text-[10px] text-[#8F9485] mt-1">Leave empty if not available</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Rate for 10x4 (₹/sq ft)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={form.rate_10x4}
                      onChange={e => setForm(f => ({ ...f, rate_10x4: e.target.value.replace(/[^0-9.]/g, '') }))}
                      placeholder="e.g., 265"
                      className="mt-1 font-mono-data"
                    />
                    <p className="text-[10px] text-[#8F9485] mt-1">Leave empty if not available</p>
                  </div>
                </div>
                
                {/* Custom Sizes Section - Multiple sizes supported */}
                <div className="p-3 bg-[#F2F4EF] rounded-lg border border-[#E2E6D9]">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold">Custom Sizes (Optional)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        const formattedSize = formatSizeInput(newCustomSize);
                        if (formattedSize) {
                          setCustomSizes([...customSizes, { size: formattedSize, rate: '' }]);
                          setNewCustomSize('');
                        }
                      }}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Size
                    </Button>
                  </div>
                  
                  {/* Add new custom size input */}
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={newCustomSize}
                      onChange={e => setNewCustomSize(formatSizeInput(e.target.value))}
                      placeholder="Type size: 9.5 4 → 9.5x4"
                      className="flex-1 h-8 text-sm font-mono"
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const formattedSize = formatSizeInput(newCustomSize);
                          if (formattedSize) {
                            setCustomSizes([...customSizes, { size: formattedSize, rate: '' }]);
                            setNewCustomSize('');
                          }
                        }
                      }}
                    />
                    <span className="text-[10px] text-[#8F9485] self-center whitespace-nowrap">Space → x</span>
                  </div>
                  
                  {/* List of custom sizes with rates */}
                  {customSizes.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <p className="text-[10px] text-[#8F9485] uppercase">Custom Sizes Added:</p>
                      {customSizes.map((cs, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-md border border-[#E2E6D9]">
                          <span className="text-sm font-medium w-20">{cs.size}</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={cs.rate}
                            onChange={e => {
                              const updated = [...customSizes];
                              updated[idx].rate = e.target.value.replace(/[^0-9.]/g, '');
                              setCustomSizes(updated);
                            }}
                            placeholder="Rate ₹/sq ft"
                            className="flex-1 h-8 font-mono-data text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setCustomSizes(customSizes.filter((_, i) => i !== idx))}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {customSizes.length === 0 && (
                    <p className="text-[10px] text-[#8F9485]">
                      Add custom sizes like 9.5x4, 12x4, etc. for special dimensions
                    </p>
                  )}
                </div>
              </div>

              {/* Stock */}
              <div className="p-3 bg-[#FEF3C7] rounded-lg border border-[#F59E0B]/30">
                <Label className="text-xs font-semibold text-[#B45309]">Stock (Optional)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={form.stock_sheets}
                    onChange={e => setForm(f => ({ ...f, stock_sheets: e.target.value.replace(/[^0-9]/g, '') }))}
                    placeholder="0"
                    className="w-24 font-mono-data"
                  />
                  <span className="text-sm text-[#5C6150]">sheets</span>
                </div>
                <p className="text-[10px] text-[#8F9485] mt-1">
                  If stock {'>'} 0, "Ready in Stock" will be shown
                </p>
              </div>
            </>
          )}

          {/* Collection Mode - Multiple Codes */}
          {collectionMode && (
            <div className="space-y-4">
              {/* Size Selection - BEFORE adding codes - MULTIPLE custom sizes supported */}
              <div className="p-3 bg-[#F7EED8] rounded-lg border border-[#DAA520]/30">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#8B4513]">
                  Available Sizes (select sizes before adding codes)
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {/* Standard sizes */}
                  {VENEER_SIZES.map(size => (
                    <button
                      key={size.id}
                      type="button"
                      onClick={() => {
                        setSelectedSizes(prev => 
                          prev.includes(size.id) 
                            ? prev.filter(s => s !== size.id)
                            : [...prev, size.id]
                        );
                      }}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        selectedSizes.includes(size.id)
                          ? 'bg-[#8B4513] text-white'
                          : 'bg-white text-[#5C6150] border border-[#E2E6D9] hover:bg-[#F9FAF7]'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                  
                  {/* Show added custom sizes as removable chips */}
                  {customCollectionSizes.map(size => (
                    <div key={size} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-[#8B4513] text-white">
                      <span>{size}</span>
                      <button
                        type="button"
                        onClick={() => removeCustomCollectionSize(size)}
                        className="ml-1 hover:text-red-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Custom button */}
                  <button
                    type="button"
                    onClick={() => setShowCustomSizeInput(prev => !prev)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      showCustomSizeInput
                        ? 'bg-[#8B4513] text-white'
                        : 'bg-white text-[#5C6150] border border-[#E2E6D9] hover:bg-[#F9FAF7]'
                    }`}
                  >
                    + Custom
                  </button>
                </div>
                
                {/* Custom size input - for adding multiple custom sizes */}
                {showCustomSizeInput && (
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <Input
                      value={newCollectionCustomSize}
                      onChange={e => setNewCollectionCustomSize(formatSizeInput(e.target.value))}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomCollectionSize();
                        }
                      }}
                      placeholder="Type size: 9.5 4 → 9.5x4"
                      className="w-36 h-8 text-xs font-mono"
                      autoFocus
                      data-testid="custom-size-input-collection"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="h-8 text-xs"
                      onClick={addCustomCollectionSize}
                      data-testid="add-custom-size-collection-btn"
                    >
                      Add
                    </Button>
                    <span className="text-[10px] text-[#8F9485]">Space → x (e.g., "9.5 4" → "9.5x4")</span>
                  </div>
                )}
                <p className="text-[10px] text-[#8F9485] mt-1">
                  Only selected size columns will appear below
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wide text-[#556B2F]">
                  Codes & Rates ({collectionCodes.length})
                </Label>
                <Button type="button" variant="outline" size="sm" onClick={addCollectionCode}>
                  <Plus className="w-3 h-3 mr-1" /> Add Code
                </Button>
              </div>

              <div className="bg-[#F2F4EF] p-3 rounded-lg space-y-2 max-h-64 overflow-y-auto overflow-x-auto">
                {/* Dynamic Header based on selected sizes */}
                <div className="grid gap-2 text-xs font-semibold text-[#5C6150] uppercase min-w-max" 
                     style={{ gridTemplateColumns: `minmax(80px, 3fr) ${selectedSizes.includes('8x4') ? 'minmax(60px, 2fr)' : ''} ${selectedSizes.includes('10x4') ? 'minmax(60px, 2fr)' : ''} ${customCollectionSizes.map(() => 'minmax(60px, 2fr)').join(' ')} minmax(60px, 2fr) 40px` }}>
                  <div>Code</div>
                  {selectedSizes.includes('8x4') && <div>8x4 Rate</div>}
                  {selectedSizes.includes('10x4') && <div>10x4 Rate</div>}
                  {customCollectionSizes.map(size => (
                    <div key={size}>{size}</div>
                  ))}
                  <div>Stock</div>
                  <div></div>
                </div>

                {/* Dynamic Code rows based on selected sizes */}
                {collectionCodes.map((item, idx) => (
                  <div key={idx} className="grid gap-2 items-center min-w-max"
                       style={{ gridTemplateColumns: `minmax(80px, 3fr) ${selectedSizes.includes('8x4') ? 'minmax(60px, 2fr)' : ''} ${selectedSizes.includes('10x4') ? 'minmax(60px, 2fr)' : ''} ${customCollectionSizes.map(() => 'minmax(60px, 2fr)').join(' ')} minmax(60px, 2fr) 40px` }}>
                    <Input
                      value={item.code}
                      onChange={e => updateCollectionCode(idx, 'code', e.target.value.toUpperCase())}
                      placeholder="Code"
                      className="h-9 font-mono text-sm"
                    />
                    {selectedSizes.includes('8x4') && (
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.rate_8x4}
                        onChange={e => updateCollectionCode(idx, 'rate_8x4', e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="₹"
                        className="h-9 font-mono-data text-sm"
                      />
                    )}
                    {selectedSizes.includes('10x4') && (
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.rate_10x4}
                        onChange={e => updateCollectionCode(idx, 'rate_10x4', e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="₹"
                        className="h-9 font-mono-data text-sm"
                      />
                    )}
                    {customCollectionSizes.map(size => (
                      <Input
                        key={size}
                        type="text"
                        inputMode="decimal"
                        value={item.custom_rates?.[size] || ''}
                        onChange={e => updateCollectionCodeCustomRate(idx, size, e.target.value.replace(/[^0-9.]/g, ''))}
                        placeholder="₹"
                        className="h-9 font-mono-data text-sm"
                      />
                    ))}
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={item.stock}
                      onChange={e => updateCollectionCode(idx, 'stock', e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="0"
                      className="h-9 font-mono-data text-sm"
                    />
                    <div className="flex justify-end">
                      {collectionCodes.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCollectionCode(idx)}
                          className="h-8 w-8 p-0 text-[#B93838]"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#8F9485]">
                Add {collectionCodes.length} code(s) • Leave rate/stock empty if not available
              </p>
            </div>
          )}

          {/* Per-product brand visibility in customer PDF */}
          <div className="flex items-start gap-2 p-3 rounded-md border border-[#E2E6D9] bg-[#F9FAF7]">
            <input
              id="vn-show-brand-in-pdf"
              type="checkbox"
              checked={form.show_brand_in_pdf !== false}
              onChange={(e) => setForm(f => ({ ...f, show_brand_in_pdf: e.target.checked }))}
              className="mt-0.5 w-4 h-4 accent-[#556B2F]"
              data-testid="form-show-brand-in-pdf"
            />
            <label htmlFor="vn-show-brand-in-pdf" className="text-xs leading-tight cursor-pointer select-none">
              <span className="font-semibold text-[#1A1C16]">Show brand name in customer PDF</span>
              <p className="text-[#5C6150] text-[10px] mt-0.5">
                Keep this OFF for natural veneers where the brand is hidden by default.
              </p>
            </label>
          </div>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#556B2F] hover:bg-[#465926] text-white h-11"
          >
            {saving ? 'Saving...' : editProduct ? 'Update Veneer' : collectionMode ? `Add ${collectionCodes.filter(c => c.code.trim()).length} Veneers` : 'Add Veneer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
